import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { CurrencyId } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  type SolanaCollectionNftStandard,
  type SolanaItemOfferRequest,
  itemOfferFactoryReatom,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import { getTransactionErrorMessage } from "../events/transaction-event"

export interface SolanaItemOfferActionParams {
  itemMint: string
  nftStandard: SolanaCollectionNftStandard
  currency: CurrencyId
  price: string
}

export const SolanaItemOfferTransactionMoleculeReatom = molecule((mol) => {
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
  const { solanaAddress: solanaAddressAtom } = mol(WalletConnectMoleculeReatom)

  const itemOfferActionFactory = () => {
    const itemOfferAction = action(
      async (params: SolanaItemOfferActionParams) => {
        const { itemMint, nftStandard, currency, price } = params
        const from = solanaAddressAtom()
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error(
            "Solana wallet address is not available for item offer",
          )
        }

        const itemOffer = itemOfferFactoryReatom(container)
        const request: SolanaItemOfferRequest = {
          from,
          itemMint,
          nftStandard,
          price: {
            currencyId: currency,
            value: price,
          },
        }

        const result = await wrap(itemOffer(request))
        return { txId: result.txId }
      },
      "solanaItemOfferAction",
    ).extend(
      withAsync(),
      // onCall: show loading toast immediately (dialog closes right after submit)
      withCallHook(() => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Offer"
              main={<Trans>Offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
      }),
    )

    itemOfferAction.onReject.extend(
      withCallHook(({ error }) => {
        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Offer"
            main={<Trans>Offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    itemOfferAction.onFulfill.extend(
      withCallHook(({ payload }) => {
        const { txId } = payload
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Offer"
            main={<Trans>Offer sent</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={txId}
                blockchain={Blockchain.SOLANA}
              />
            }
          />
        ))
      }),
    )

    return itemOfferAction
  }

  return { itemOfferActionFactory }
})
